import React, { useEffect, useState } from "react";
import { Card, Button, Spin, message } from "antd";
import { MenuOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import axios from "axios";
import "../../../config";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate } from "react-router-dom"; // Add this import

// Inline SortableItem for simplicity
function SortableItem({ id, children, image }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? "#e6f7ff" : "#fff",
    border: "1px solid #f0f0f0",
    borderRadius: 6,
    marginBottom: 8,
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    boxShadow: isDragging ? "0 2px 8px rgba(24,144,255,0.15)" : "none",
    cursor: "grab",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <span
        {...listeners}
        style={{
          marginRight: 16,
          color: "#1890ff",
          fontSize: 18,
          cursor: "grab",
        }}
      >
        <MenuOutlined />
      </span>
      {image && (
        <img
          src={image}
          alt=""
          style={{
            width: 40,
            height: 40,
            objectFit: "cover",
            borderRadius: 4,
            marginRight: 16,
          }}
        />
      )}
      <span style={{ flex: 1 }}>{children}</span>
    </div>
  );
}

const ArrangeCategorySequence = ({ onClose }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); // Add this line

  useEffect(() => {
    setLoading(true);
    getListofProductCategory();
    // eslint-disable-next-line
  }, []);

  async function getListofProductCategory() {
    setLoading(true);
    const tokenString = localStorage.getItem("token");
    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          "api/ProductCategory/ListofProductCategory",
        headers: {
          Authorization: "Bearer " + tokenString,
        },
      };
      axios
        .request(config)
        .then((response) => {
          // Combine all categories from all sections, then sort by SortingNo
          const flatCategories = (response.data || [])
            .flatMap((section) =>
              section.data.map((cat) => ({
                ...cat,
                sectionTitle: section.title,
                SortingNumber: cat.SortingNo, // Use SortingNo from API as SortingNumber
              }))
            )
            .sort((a, b) => (a.SortingNumber ?? 0) - (b.SortingNumber ?? 0)); // Sort by SortingNumber

          setCategories(flatCategories);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } catch (err) {
      setLoading(false);
    }
  }

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Drag and drop logic similar to ProductAdminList.js
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = active.id;
    const overId = over.id;

    if (activeId !== overId) {
      setCategories((items) => {
        const oldIndex = items.findIndex(
          (item) => String(item.ProductCategoryID) === activeId
        );
        const newIndex = items.findIndex(
          (item) => String(item.ProductCategoryID) === overId
        );

        const reorderedItems = arrayMove(items, oldIndex, newIndex);

        const updatedItems = reorderedItems.map((item, index) => ({
          ...item,
          SortingNumber: index + 1,
        }));

        updateSortingNumbersAPI(updatedItems);

        return updatedItems;
      });
    }
  };

  // Update sorting numbers for all categories using a batch API call
  const updateSortingNumbersAPI = async (updatedCategories) => {
    try {
      const tokenString = localStorage.getItem("token");
      // Prepare the payload as a list of { ProductCategoryID, SortingNumber }
      const payload = updatedCategories.map((cat) => ({
        ProductCategoryID: cat.ProductCategoryID,
        SortingNumber: cat.SortingNumber,
      }));

      // Call the batch update API (replace with your actual batch endpoint if available)
      await axios.post(
        `${global.config.APIURL.BaseURL}api/ProductCategory/SetMultipleProductCatSortingNumber`,
        payload,
        {
          headers: {
            Authorization: "Bearer " + tokenString,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      // Optionally show error
    }
  };

  // Assign default sequence in a single batch call
  const handleAssignDefaultSequence = async () => {
    const tokenString = localStorage.getItem("token");
    const payload = categories.map((cat, idx) => ({
      ProductCategoryID: cat.ProductCategoryID,
      SortingNumber: idx + 1,
    }));
    try {
      await axios.post(
        `${global.config.APIURL.BaseURL}api/ProductCategory/SetMultipleProductCatSortingNumber`,
        payload,
        {
          headers: {
            Authorization: "Bearer " + tokenString,
            "Content-Type": "application/json",
          },
        }
      );
      message.success("Default sequence assigned!");
      getListofProductCategory();
    } catch {
      message.error("Failed to assign default sequence.");
    }
  };

  return (
    <div style={{ maxWidth: 500, margin: "0 auto", marginTop: 32 }}>
      {/* Back button outside the Card, top left */}
      <Button
        onClick={() => navigate("/AdminCategory")}
        type="default"
        size="small"
        icon={<ArrowLeftOutlined />}
        style={{ marginBottom: 16 }}
      >
        Back to Category List
      </Button>
      <Card
        title="Rearrange Categories"
        styles={{ padding: 0 }}
        extra={
          <Button
            onClick={handleAssignDefaultSequence}
            type="primary"
            size="small"
          >
            Assign Default Sequence
          </Button>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={categories.map((cat) => String(cat.ProductCategoryID))}
              strategy={verticalListSortingStrategy}
            >
              <div style={{ padding: 16, minHeight: 300 }}>
                {categories.map((cat) => (
                  <SortableItem
                    key={cat.ProductCategoryID}
                    id={String(cat.ProductCategoryID)}
                    image={cat.CategoryImage}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span style={{ flex: 1 }}>{cat.CategoryName}</span>
                      <span
                        style={{
                          minWidth: 32,
                          textAlign: "right",
                          color: "#888",
                          fontWeight: 500,
                          fontSize: 14,
                          marginLeft: 8,
                        }}
                      >
                        #{cat.SortingNumber}
                      </span>
                    </div>
                  </SortableItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </Card>
    </div>
  );
};

export default ArrangeCategorySequence;
